import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { sendPassRateEvent } from '~analytics/index';
import { translate } from '~l10n/client';
import { SmallButton } from '~shared/components/buttons';
import { IconX } from '~shared/components/icons/standard';
import TextInput from '~shared/components/TextInput/TextInput';
import { useUpdatePassRate } from '~teacher/hook';

interface PassRateContentProps {
  onClose?: () => void;
  currentThreshold?: number;
  activityInstanceId: number;
}

export function isValidPercentageInput(newValue: string): boolean {
  // Allow empty string
  if (newValue === '') return true;

  // Normalize comma to period for consistent validation
  const normalized = newValue.replace(',', '.');

  // Allow digits and one decimal point
  if (!/^\d*\.?\d*$/.test(normalized)) return false;

  // Block leading zeros followed by more digits (except decimals)
  if (
    normalized.length > 1 &&
    normalized.startsWith('0') &&
    normalized[1] !== '.'
  ) {
    return false;
  }

  // Block values that would be >= 100 (after parsing)
  const numValue = parseFloat(normalized);
  if (!isNaN(numValue) && numValue >= 100) {
    return false;
  }

  // Limit decimal places to 2
  if (normalized.includes('.')) {
    const decimalPart = normalized.split('.')[1];
    if (decimalPart && decimalPart.length > 2) {
      return false;
    }
  }

  return true;
}

export function PassThresholdPopoverContent({
  onClose,
  currentThreshold,
  activityInstanceId,
}: PassRateContentProps) {
  const [threshold, setThreshold] = useState<string>(
    currentThreshold?.toString() ?? ''
  );
  const [isValid, setIsValid] = useState<boolean>(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const updatePassRateMutation = useUpdatePassRate();

  // Handle autofocus
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleClear = (event: React.MouseEvent) => {
    // Prevent popover from closing
    event.stopPropagation();
    setThreshold('');
    setIsValid(true);
    inputRef.current?.focus();
    sendPassRateEvent({
      action: 'clear_field',
    });
  };

  const handleSave = () => {
    if (threshold) {
      if (isValid) {
        // Normalize comma to period before parsing
        const normalizedValue = threshold.replace(',', '.');
        updatePassRateMutation.mutate({
          activityInstanceId,
          passRate: parseFloat(normalizedValue),
        });
      }
    } else {
      // Clear pass rate when threshold is empty
      updatePassRateMutation.mutate({
        activityInstanceId,
        passRate: null,
      });
    }

    onClose?.();
  };

  const onPassThresholdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    if (!isValidPercentageInput(value)) {
      return;
    }

    setThreshold(value);
    setIsValid(true);
  };

  const handleBlur = () => {
    if (threshold && isValid) {
      // Normalize comma to period before parsing
      const normalizedValue = threshold.replace(',', '.');
      const formatted = parseFloat(normalizedValue);

      if (!isNaN(formatted)) {
        setThreshold(formatted.toString());
      }
    }
  };

  return (
    <div className="flex flex-col align-left gap-4 text-base">
      <p className="text-blue-900">{translate('Passing Threshold')}</p>

      <div className="relative w-1/2">
        <TextInput
          ref={inputRef}
          id="passing-threshold-input"
          withBorder={false}
          value={threshold}
          onChange={onPassThresholdChange}
          onBlur={handleBlur}
          placeholder={translate('None')}
          className="pr-8 bg-grey-50"
          inputMode="decimal"
          title="Enter a percentage between 0 and 99.99"
        />
        {threshold && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-grey-800 bg-transparent border-0 cursor-pointer p-1 hover:text-grey-600"
          >
            <IconX />
          </button>
        )}
      </div>

      <p className="text-blue-600">
        {translate(
          'This value isn’t visible to students and is only applied to this report.'
        )}
      </p>

      <div className="flex justify-start">
        <SmallButton appearance="primary" onClick={handleSave}>
          {translate('Save')}
        </SmallButton>
      </div>
    </div>
  );
}
